import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { zIndex } from "@/styles/variants";
import { ui } from "@/strings";
import PiQuestionMarkCircleStroke from "@markeroo/icons/src/stroke/alerts/PiQuestionMarkCircleStroke";

export interface TooltipProps {
  content: string;
  children?: React.ReactNode;
  fullWidth?: boolean;
  /** When true, tooltip will not show on hover/focus */
  disabled?: boolean;
}

type Placement = "above" | "below";

const TOOLTIP_GAP = 8; // px gap between trigger and tooltip

export function Tooltip({ content, children, fullWidth = false, disabled = false }: TooltipProps) {
  const [isTriggerActive, setIsTriggerActive] = useState(false);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLSpanElement | null>(null);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    placement: Placement;
    triggerBottom: number;
    triggerTop: number;
  } | null>(null);
  const [isPositioned, setIsPositioned] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const showTooltip = useCallback(() => {
    clearHideTimeout();
    setIsTriggerActive(true);
  }, [clearHideTimeout]);

  // Schedule hide with delay to allow mouse to reach tooltip
  const scheduleTriggerHide = useCallback(() => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setIsTriggerActive(false);
    }, 150); // 150ms delay to allow mouse to reach tooltip
  }, [clearHideTimeout]);

  const hideTooltip = useCallback(() => {
    clearHideTimeout();
    setIsTriggerActive(false);
    setIsHoveringTooltip(false);
  }, [clearHideTimeout]);

  // Show tooltip when trigger or tooltip itself is hovered/focused (and not disabled)
  const shouldShow = !disabled && (isTriggerActive || isHoveringTooltip);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => clearHideTimeout();
  }, [clearHideTimeout]);

  const updatePosition = () => {
    const el = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const padding = 8; // minimum padding from viewport edge

    let left = rect.left + rect.width / 2;
    let placement: Placement = "above";
    let top = rect.top;

    if (tooltip) {
      const tooltipRect = tooltip.getBoundingClientRect();
      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Determine vertical placement: prefer above, but use below if not enough space
      const spaceAbove = rect.top;
      const spaceBelow = viewportHeight - rect.bottom;

      if (spaceAbove < tooltipHeight + TOOLTIP_GAP + padding && spaceBelow > spaceAbove) {
        // Not enough space above and more space below - position below
        placement = "below";
        top = rect.bottom + TOOLTIP_GAP;
      } else {
        // Position above (default)
        placement = "above";
        top = rect.top - TOOLTIP_GAP;
      }

      // Adjust horizontal position if tooltip would overflow viewport
      const tooltipLeft = left - tooltipWidth / 2;
      const tooltipRight = left + tooltipWidth / 2;

      if (tooltipLeft < padding) {
        left = tooltipWidth / 2 + padding;
      } else if (tooltipRight > viewportWidth - padding) {
        left = viewportWidth - tooltipWidth / 2 - padding;
      }
    }

    setCoords({
      top: Math.max(padding, top),
      left,
      placement,
      triggerTop: rect.top,
      triggerBottom: rect.bottom,
    });
  };

  useLayoutEffect(() => {
    if (shouldShow) {
      setIsPositioned(false);
      updatePosition();
      // Recalculate on next frame after tooltip renders and can be measured,
      // then mark as positioned to make it visible
      requestAnimationFrame(() => {
        updatePosition();
        setIsPositioned(true);
      });
    } else {
      setIsPositioned(false);
    }
  }, [shouldShow]);

  useEffect(() => {
    if (!shouldShow) return;
    const handle = () => updatePosition();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    // Hide tooltip on any touch event (mobile) - this prevents tooltips from
    // persisting when user taps elsewhere or opens a modal
    window.addEventListener("touchstart", hideTooltip, true);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
      window.removeEventListener("touchstart", hideTooltip, true);
    };
  }, [shouldShow, hideTooltip]);

  const wrapperClass = fullWidth ? "relative flex w-full" : "relative inline-flex items-center";
  const triggerClass = fullWidth ? "flex w-full" : "inline-flex items-center";

  return (
    <span className={wrapperClass}>
      <span
        className={triggerClass}
        onMouseEnter={showTooltip}
        onMouseLeave={scheduleTriggerHide}
        onMouseDown={hideTooltip}
        ref={triggerRef}
      >
        {children || (
          <PiQuestionMarkCircleStroke
            size={16}
            className="cursor-help text-text-theme-muted hover:text-text-theme-secondary"
            aria-label={ui.labels.help}
          />
        )}
      </span>
      {coords
        ? createPortal(
            <>
              {/* Invisible bridge element that fills the gap between trigger and tooltip */}
              {shouldShow && (
                <span
                  style={{
                    position: "fixed",
                    zIndex: zIndex.tooltip,
                    left: coords.left,
                    transform: "translateX(-50%)",
                    // Position and size to fill the gap
                    top: coords.placement === "above" ? coords.top : coords.triggerBottom,
                    height: TOOLTIP_GAP,
                    width: 48, // Wide enough to catch mouse moving to tooltip
                    pointerEvents: "auto",
                  }}
                  onMouseEnter={() => {
                    clearHideTimeout();
                    setIsHoveringTooltip(true);
                  }}
                  onMouseLeave={() => {
                    setIsHoveringTooltip(false);
                    scheduleTriggerHide();
                  }}
                  aria-hidden="true"
                />
              )}
              {/* Tooltip content */}
              <span
                ref={tooltipRef}
                style={{
                  position: "fixed",
                  zIndex: zIndex.tooltip,
                  top: coords.top,
                  left: coords.left,
                  transform: coords.placement === "above"
                    ? "translate(-50%, -100%)"
                    : "translate(-50%, 0)",
                  pointerEvents: shouldShow ? "auto" : "none",
                  visibility: shouldShow ? "visible" : "hidden",
                }}
                onMouseEnter={() => {
                  clearHideTimeout();
                  setIsHoveringTooltip(true);
                }}
                onMouseLeave={() => {
                  setIsHoveringTooltip(false);
                  scheduleTriggerHide();
                }}
              >
                <span
                  className="block max-w-xs rounded-lg border border-border bg-brand-950 px-3 py-2 text-sm text-white shadow-lg transition-opacity duration-100"
                  style={{
                    opacity: shouldShow && isPositioned ? 1 : 0,
                  }}
                >
                  {content.split('\n').map((line, i, arr) => (
                    <span key={i}>
                      {line}
                      {i < arr.length - 1 && <br />}
                    </span>
                  ))}
                </span>
              </span>
            </>,
            document.body
          )
        : null}
    </span>
  );
}
